import React, { useRef, useEffect, useState } from "react"
import Button from "./ui/Button"

const COLORS = ["#0f172a", "#dc2626", "#2563eb", "#16a34a", "#ea580c"]
const PEN_SIZES = [
  { label: "Thin", value: 2 },
  { label: "Medium", value: 4 },
  { label: "Thick", value: 7 },
]

// Host draws and broadcasts over Agora's RTC data channel (audience roles
// can't reliably send data messages, only receive — so drawing is
// host-only for now). Whenever `attendeeCount` goes up while the board is
// open, the host re-sends the full stroke history — as one message PER
// STROKE, not one giant blob — so late joiners aren't stuck looking at a
// blank canvas without risking Agora's per-message size limit.
const LiveWhiteboard = ({ client, isHost, attendeeCount }) => {
  const canvasRef = useRef(null)
  const containerRef = useRef(null)
  const strokesRef = useRef([]) // [{ id, color, width, points: [{x,y}] }] — points normalized 0..1
  const activeStrokeIdRef = useRef(null)
  const drawingRef = useRef(false)
  const lastSendRef = useRef(0)
  const prevAttendeeCountRef = useRef(attendeeCount || 0)
  const [color, setColor] = useState(COLORS[0])
  const [penSize, setPenSize] = useState(PEN_SIZES[1].value)

  const send = (payload) => {
    if (!client) return
    client.sendStreamMessage(JSON.stringify(payload)).catch((err) => {
      // Previously swallowed — surfacing this is how we'll actually see if
      // a message got rejected (e.g. Agora's per-message data-channel size
      // cap) instead of it failing invisibly.
      console.error("[LiveWhiteboard] sendStreamMessage failed:", err)
    })
  }

  // Smooths a stroke by drawing quadratic curves through the midpoints of
  // consecutive points, instead of straight lineTo segments between raw
  // mouse/touch samples — the straight-segment approach is what produces
  // the jagged, angular look at normal drawing speed.
  const drawSmoothStroke = (ctx, canvas, points, strokeColor, strokeWidth) => {
    if (!points?.length) return
    ctx.strokeStyle = strokeColor
    ctx.lineWidth = strokeWidth
    ctx.lineCap = "round"
    ctx.lineJoin = "round"

    const toPixel = (pt) => ({ x: pt.x * canvas.width, y: pt.y * canvas.height })

    if (points.length < 3) {
      const [a, b] = points.map(toPixel)
      ctx.beginPath()
      ctx.moveTo(a.x, a.y)
      ctx.lineTo((b || a).x, (b || a).y)
      ctx.stroke()
      return
    }

    const pixelPoints = points.map(toPixel)
    ctx.beginPath()
    ctx.moveTo(pixelPoints[0].x, pixelPoints[0].y)
    for (let i = 1; i < pixelPoints.length - 1; i++) {
      const mid = { x: (pixelPoints[i].x + pixelPoints[i + 1].x) / 2, y: (pixelPoints[i].y + pixelPoints[i + 1].y) / 2 }
      ctx.quadraticCurveTo(pixelPoints[i].x, pixelPoints[i].y, mid.x, mid.y)
    }
    ctx.stroke()
  }

  const redrawAll = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    strokesRef.current.forEach((stroke) => drawSmoothStroke(ctx, canvas, stroke.points, stroke.color, stroke.width || 3))
  }

  // Live incremental draw while the host is actively drawing — redraws just
  // the active stroke smoothly on every point, rather than a raw straight
  // segment per mouse-move event.
  const redrawActiveStroke = () => {
    const canvas = canvasRef.current
    const stroke = strokesRef.current.find((s) => s.id === activeStrokeIdRef.current)
    if (!canvas || !stroke) return
    redrawAll()
  }

  // — size the canvas to match its displayed area —
  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    const resize = () => {
      const rect = container.getBoundingClientRect()
      canvas.width = rect.width
      canvas.height = rect.height
      redrawAll()
    }
    resize()

    const observer = new ResizeObserver(resize)
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  // — receive whiteboard messages over the Agora data channel —
  useEffect(() => {
    if (!client) return

    const handleMessage = (_uid, data) => {
      let payload
      try {
        payload = JSON.parse(new TextDecoder().decode(data))
      } catch {
        return
      }
      if (!payload?.type?.startsWith("wb-")) return

      if (payload.type === "wb-stroke-start") {
        strokesRef.current.push({ id: payload.strokeId, color: payload.color, width: payload.width || 3, points: [payload.point] })
        return
      }
      if (payload.type === "wb-stroke-point") {
        const stroke = strokesRef.current.find((s) => s.id === payload.strokeId)
        if (!stroke) return
        stroke.points.push(payload.point)
        redrawAll()
        return
      }
      if (payload.type === "wb-clear") {
        strokesRef.current = []
        redrawAll()
        return
      }
      if (payload.type === "wb-sync-clear" && !isHost) {
        strokesRef.current = []
        redrawAll()
        return
      }
      if (payload.type === "wb-sync-stroke" && !isHost) {
        strokesRef.current.push(payload.stroke)
        redrawAll()
      }
    }

    client.on("stream-message", handleMessage)
    return () => client.off("stream-message", handleMessage)
  }, [client, isHost])

  // — host: catch up late joiners, one stroke per message —
  useEffect(() => {
    if (!isHost) return
    if ((attendeeCount || 0) > prevAttendeeCountRef.current) {
      send({ type: "wb-sync-clear" })
      strokesRef.current.forEach((stroke) => send({ type: "wb-sync-stroke", stroke }))
    }
    prevAttendeeCountRef.current = attendeeCount || 0
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendeeCount, isHost, client])

  const getPoint = (e) => {
    const rect = containerRef.current.getBoundingClientRect()
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const clientY = e.touches ? e.touches[0].clientY : e.clientY
    return { x: (clientX - rect.left) / rect.width, y: (clientY - rect.top) / rect.height }
  }

  const handlePointerDown = (e) => {
    if (!isHost) return
    e.preventDefault()
    const point = getPoint(e)
    const strokeId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    activeStrokeIdRef.current = strokeId
    drawingRef.current = true
    strokesRef.current.push({ id: strokeId, color, width: penSize, points: [point] })
    send({ type: "wb-stroke-start", strokeId, color, width: penSize, point })
  }

  const handlePointerMove = (e) => {
    if (!isHost || !drawingRef.current) return
    e.preventDefault()
    const point = getPoint(e)
    const stroke = strokesRef.current.find((s) => s.id === activeStrokeIdRef.current)
    if (!stroke) return
    stroke.points.push(point)
    redrawActiveStroke()

    const now = Date.now()
    if (now - lastSendRef.current > 16) {
      lastSendRef.current = now
      send({ type: "wb-stroke-point", strokeId: activeStrokeIdRef.current, point })
    }
  }

  const handlePointerUp = () => {
    if (!isHost) return
    drawingRef.current = false
    activeStrokeIdRef.current = null
  }

  const handleClear = () => {
    strokesRef.current = []
    redrawAll()
    send({ type: "wb-clear" })
  }

  return (
    <div className="bg-white rounded-xl border border-border overflow-hidden">
      <div ref={containerRef} className="relative w-full aspect-video">
        <canvas
          ref={canvasRef}
          className={`absolute inset-0 w-full h-full ${isHost ? "cursor-crosshair" : ""}`}
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
        />
      </div>
      {isHost && (
        <div className="flex items-center justify-between gap-3 p-2 border-t border-border bg-card flex-wrap">
          <div className="flex items-center gap-1.5">
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`w-6 h-6 rounded-full border-2 ${color === c ? "border-accent" : "border-transparent"}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            {PEN_SIZES.map((size) => (
              <button
                key={size.value}
                onClick={() => setPenSize(size.value)}
                title={size.label}
                className={`w-7 h-7 rounded-md flex items-center justify-center border ${penSize === size.value ? "border-accent bg-accent/10" : "border-transparent"}`}
              >
                <span className="rounded-full bg-foreground" style={{ width: size.value + 2, height: size.value + 2 }} />
              </button>
            ))}
          </div>
          <Button variant="ghost" size="sm" iconName="Eraser" iconPosition="left" onClick={handleClear}>
            Clear
          </Button>
        </div>
      )}
    </div>
  )
}

export default LiveWhiteboard